/**
 * PHASE1-AMAZON-AU-MERCHANT-SHIPPING-GROUP-021E
 *
 * Listing-scoped Amazon MFN shipping-policy contract. Parses INJECTED
 * seller-specific Product Type Definition fixtures only. Never calls Amazon.
 * Never writes Listings Items. Never treats Product Master or
 * products.shipping_profile as merchant_shipping_group.
 */

import type { MarketplaceReadinessStatus, ProviderReadiness } from "@/lib/marketplace-readiness";
import { missingShippingFields, type ShippingDimensions } from "@/lib/shipping-facts";

const AMAZON_PROVIDER = "amazon";
const AMAZON_AU_MARKETPLACE_ID = "A39IBJ37TRP1C6";

type AmazonSchemaIdentityLite = {
  productType: string;
  marketplaceId: string;
  productTypeVersion: string | null;
  schemaChecksum: string | null;
  fetchedAt: string | null;
  freshness: string;
};

export const AMAZON_LISTING_CONFIG_NAMESPACE = "amazon" as const;

export const AMAZON_FULFILLMENT_MERCHANT = "MERCHANT_FULFILLED" as const;
export const AMAZON_FULFILLMENT_FBA = "FULFILLED_BY_AMAZON" as const;
export type AmazonFulfillmentMethodIntent =
  | typeof AMAZON_FULFILLMENT_MERCHANT
  | typeof AMAZON_FULFILLMENT_FBA;

export const AMAZON_SHIPPING_POLICY_EXPLICIT = "EXPLICIT_TEMPLATE" as const;
export const AMAZON_SHIPPING_POLICY_DEFAULT = "AMAZON_DEFAULT" as const;
export const AMAZON_SHIPPING_POLICY_UNSET = "UNSET" as const;
export type AmazonShippingPolicyIntentKind =
  | typeof AMAZON_SHIPPING_POLICY_EXPLICIT
  | typeof AMAZON_SHIPPING_POLICY_DEFAULT
  | typeof AMAZON_SHIPPING_POLICY_UNSET;

export type AmazonMerchantShippingTemplate = {
  key: string;
  displayName: string;
};

export type AmazonMerchantShippingParseOk = {
  ok: true;
  error: null;
  code: null;
  templates: AmazonMerchantShippingTemplate[];
  merchantShippingGroupPresent: boolean;
  merchantShippingGroupRequired: boolean;
  leadTimeToShip: { present: boolean; required: boolean };
  fulfillmentChannelCodes: string[];
  itemPackageWeight: { present: boolean; required: boolean };
  itemPackageDimensions: { present: boolean; required: boolean };
  identity: { productTypeVersion: string; schemaChecksum: string };
};

export type AmazonMerchantShippingParseFail = {
  ok: false;
  code: "ENUM_NAME_MISMATCH" | "DUPLICATE_TEMPLATE_KEY" | "MALFORMED_ATTRIBUTE";
  error: string;
  templates: [];
  identity: { productTypeVersion: string | null; schemaChecksum: string | null };
};

export type AmazonMerchantShippingParseResult = AmazonMerchantShippingParseOk | AmazonMerchantShippingParseFail;

export type AmazonListingShippingConfig = {
  fulfillmentMethod: AmazonFulfillmentMethodIntent;
  policyIntent: AmazonShippingPolicyIntentKind;
  /** Provider enum KEY is the only durable authority for EXPLICIT_TEMPLATE. */
  merchantShippingGroupKey: string | null;
  /** Cached display name only — never authority. */
  merchantShippingGroupDisplayName: string | null;
  validatedProductTypeVersion: string | null;
  validatedSchemaChecksum: string | null;
};

export type AmazonSellerSpecificPtdInput = {
  /** Generic PTD (no sellerId) cannot establish shipping READY. */
  sellerSpecific: boolean;
  schema: unknown;
  identity: AmazonSchemaIdentityLite;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  if (!value.every((item) => typeof item === "string" && item.length > 0)) return null;
  return value as string[];
}

function findNamedNode(root: unknown, name: string): Record<string, unknown> | null {
  const rec = asRecord(root);
  if (!rec) return null;
  if (name in rec) {
    const direct = asRecord(rec[name]);
    if (direct) return direct;
  }
  const properties = asRecord(rec.properties);
  if (properties) {
    const fromProps = asRecord(properties[name]);
    if (fromProps) return fromProps;
  }
  const items = rec.items;
  if (items) {
    const nested = findNamedNode(items, name);
    if (nested) return nested;
  }
  const defs = asRecord(rec.$defs);
  if (defs) {
    for (const node of Object.values(defs)) {
      const nested = findNamedNode(node, name);
      if (nested) return nested;
    }
  }
  return null;
}

function requiredIncludes(node: Record<string, unknown> | null, key: string): boolean {
  if (!node) return false;
  const required = node.required;
  return Array.isArray(required) && required.includes(key);
}

function schemaRootRequired(schema: unknown, key: string): boolean {
  const rec = asRecord(schema);
  return requiredIncludes(rec, key);
}

function propertyPresent(schema: unknown, key: string): boolean {
  return findNamedNode(schema, key) != null;
}

function parseMerchantShippingGroup(schema: unknown): {
  present: boolean;
  required: boolean;
  templates?: AmazonMerchantShippingTemplate[];
  fail?: AmazonMerchantShippingParseFail["code"];
  error?: string;
} {
  const node = findNamedNode(schema, "merchant_shipping_group");
  const required = schemaRootRequired(schema, "merchant_shipping_group");
  if (!node) return { present: false, required };

  const items = asRecord(node.items) ?? node;
  const itemProps = asRecord(items.properties);
  const valueNode = asRecord(itemProps?.value) ?? asRecord(node.properties)?.value;
  const valueRec = asRecord(valueNode);
  if (!valueRec) {
    return {
      present: true,
      required,
      fail: "MALFORMED_ATTRIBUTE",
      error: "merchant_shipping_group is present but has no value schema",
    };
  }

  const keys = asStringArray(valueRec.enum);
  const names = asStringArray(valueRec.enumNames);
  if (keys == null && names == null) {
    return { present: true, required, templates: [] };
  }
  if (keys == null || names == null) {
    return {
      present: true,
      required,
      fail: "ENUM_NAME_MISMATCH",
      error: "merchant_shipping_group enum and enumNames must both be string arrays",
    };
  }
  if (keys.length !== names.length) {
    return {
      present: true,
      required,
      fail: "ENUM_NAME_MISMATCH",
      error: "merchant_shipping_group enum/enumNames length mismatch",
    };
  }
  const seen = new Set<string>();
  const templates: AmazonMerchantShippingTemplate[] = [];
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]!;
    if (seen.has(key)) {
      return {
        present: true,
        required,
        fail: "DUPLICATE_TEMPLATE_KEY",
        error: "merchant_shipping_group contains duplicate enum keys",
      };
    }
    seen.add(key);
    templates.push({ key, displayName: names[i]! });
  }
  return { present: true, required, templates };
}

function parseLeadTime(schema: unknown): { present: boolean; required: boolean } {
  const availability = findNamedNode(schema, "fulfillment_availability");
  if (!availability) return { present: false, required: false };
  const items = asRecord(availability.items) ?? availability;
  const present = findNamedNode(availability, "lead_time_to_ship_max_days") != null
    || findNamedNode(items, "lead_time_to_ship_max_days") != null;
  const required = requiredIncludes(items, "lead_time_to_ship_max_days")
    || schemaRootRequired(schema, "lead_time_to_ship_max_days");
  return { present, required };
}

function parseFulfillmentChannelCodes(schema: unknown): string[] {
  const availability = findNamedNode(schema, "fulfillment_availability");
  if (!availability) return [];
  const items = asRecord(availability.items) ?? availability;
  const props = asRecord(items.properties);
  const codeNode = asRecord(props?.fulfillment_channel_code);
  const codes = asStringArray(codeNode?.enum);
  return codes ?? [];
}

export function parseAmazonMerchantShippingFromPtd(
  schema: unknown,
  identity: { productTypeVersion?: string | null; schemaChecksum?: string | null },
): AmazonMerchantShippingParseResult {
  const productTypeVersion = identity.productTypeVersion?.trim() || null;
  const schemaChecksum = identity.schemaChecksum?.trim() || null;
  const group = parseMerchantShippingGroup(schema);
  if (group.fail) {
    return {
      ok: false,
      code: group.fail,
      error: group.error ?? "merchant_shipping_group is malformed",
      templates: [],
      identity: { productTypeVersion, schemaChecksum },
    };
  }
  if (!productTypeVersion || !schemaChecksum) {
    return {
      ok: false,
      code: "MALFORMED_ATTRIBUTE",
      error: "Schema identity requires productTypeVersion and checksum; SchemaLink is not durable identity",
      templates: [],
      identity: { productTypeVersion, schemaChecksum },
    };
  }
  return {
    ok: true,
    error: null,
    code: null,
    templates: group.templates ?? [],
    merchantShippingGroupPresent: group.present,
    merchantShippingGroupRequired: group.required,
    leadTimeToShip: parseLeadTime(schema),
    fulfillmentChannelCodes: parseFulfillmentChannelCodes(schema),
    itemPackageWeight: {
      present: propertyPresent(schema, "item_package_weight"),
      required: schemaRootRequired(schema, "item_package_weight"),
    },
    itemPackageDimensions: {
      present: propertyPresent(schema, "item_package_dimensions"),
      required: schemaRootRequired(schema, "item_package_dimensions"),
    },
    identity: { productTypeVersion, schemaChecksum },
  };
}

export function emptyAmazonListingShippingConfig(): AmazonListingShippingConfig {
  return {
    fulfillmentMethod: AMAZON_FULFILLMENT_MERCHANT,
    policyIntent: AMAZON_SHIPPING_POLICY_UNSET,
    merchantShippingGroupKey: null,
    merchantShippingGroupDisplayName: null,
    validatedProductTypeVersion: null,
    validatedSchemaChecksum: null,
  };
}

function amazonNs(providerConfig: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return asRecord(providerConfig?.[AMAZON_LISTING_CONFIG_NAMESPACE]) ?? {};
}

export function amazonListingShippingFromProviderConfig(
  providerConfig: Record<string, unknown> | null | undefined,
): AmazonListingShippingConfig {
  const amazon = amazonNs(providerConfig);
  const method = amazon.fulfillment_method === AMAZON_FULFILLMENT_FBA
    ? AMAZON_FULFILLMENT_FBA
    : AMAZON_FULFILLMENT_MERCHANT;
  const intentRaw = amazon.shipping_policy_intent;
  const policyIntent: AmazonShippingPolicyIntentKind =
    intentRaw === AMAZON_SHIPPING_POLICY_EXPLICIT || intentRaw === AMAZON_SHIPPING_POLICY_DEFAULT
      ? intentRaw
      : AMAZON_SHIPPING_POLICY_UNSET;
  const key = typeof amazon.merchant_shipping_group_key === "string" && amazon.merchant_shipping_group_key.trim()
    ? amazon.merchant_shipping_group_key.trim()
    : null;
  const display = typeof amazon.merchant_shipping_group_display_name === "string"
    ? amazon.merchant_shipping_group_display_name
    : null;
  return {
    fulfillmentMethod: method,
    policyIntent,
    merchantShippingGroupKey: policyIntent === AMAZON_SHIPPING_POLICY_EXPLICIT ? key : null,
    merchantShippingGroupDisplayName: policyIntent === AMAZON_SHIPPING_POLICY_EXPLICIT ? display : null,
    validatedProductTypeVersion: typeof amazon.shipping_schema_product_type_version === "string"
      ? amazon.shipping_schema_product_type_version
      : null,
    validatedSchemaChecksum: typeof amazon.shipping_schema_checksum === "string"
      ? amazon.shipping_schema_checksum
      : null,
  };
}

export function mergeAmazonListingShippingIntoProviderConfig(
  providerConfig: Record<string, unknown> | null | undefined,
  shipping: AmazonListingShippingConfig,
): Record<string, unknown> {
  const next = { ...(providerConfig ?? {}) };
  const amazon = { ...amazonNs(providerConfig) };
  delete amazon.legacy_template_id;
  amazon.fulfillment_method = shipping.fulfillmentMethod;
  amazon.shipping_policy_intent = shipping.policyIntent;
  if (shipping.policyIntent === AMAZON_SHIPPING_POLICY_EXPLICIT && shipping.merchantShippingGroupKey) {
    amazon.merchant_shipping_group_key = shipping.merchantShippingGroupKey;
    if (shipping.merchantShippingGroupDisplayName) {
      amazon.merchant_shipping_group_display_name = shipping.merchantShippingGroupDisplayName;
    } else {
      delete amazon.merchant_shipping_group_display_name;
    }
  } else {
    delete amazon.merchant_shipping_group_key;
    delete amazon.merchant_shipping_group_display_name;
  }
  if (shipping.validatedProductTypeVersion && shipping.validatedSchemaChecksum) {
    amazon.shipping_schema_product_type_version = shipping.validatedProductTypeVersion;
    amazon.shipping_schema_checksum = shipping.validatedSchemaChecksum;
  }
  next[AMAZON_LISTING_CONFIG_NAMESPACE] = amazon;
  return next;
}

export function selectAmazonExplicitShippingTemplate(
  parse: AmazonMerchantShippingParseOk,
  key: string,
): { ok: true; config: AmazonListingShippingConfig } | { ok: false; error: string } {
  const trimmed = key.trim();
  const match = parse.templates.find((row) => row.key === trimmed);
  if (!match) return { ok: false, error: "Selected Amazon shipping policy is not in the current Product Type Definition" };
  return {
    ok: true,
    config: {
      fulfillmentMethod: AMAZON_FULFILLMENT_MERCHANT,
      policyIntent: AMAZON_SHIPPING_POLICY_EXPLICIT,
      merchantShippingGroupKey: match.key,
      merchantShippingGroupDisplayName: match.displayName,
      validatedProductTypeVersion: parse.identity.productTypeVersion,
      validatedSchemaChecksum: parse.identity.schemaChecksum,
    },
  };
}

export function selectAmazonDefaultShippingPolicy(parse: AmazonMerchantShippingParseOk): AmazonListingShippingConfig {
  return {
    fulfillmentMethod: AMAZON_FULFILLMENT_MERCHANT,
    policyIntent: AMAZON_SHIPPING_POLICY_DEFAULT,
    merchantShippingGroupKey: null,
    merchantShippingGroupDisplayName: null,
    validatedProductTypeVersion: parse.identity.productTypeVersion,
    validatedSchemaChecksum: parse.identity.schemaChecksum,
  };
}

export function amazonFulfillmentMethodSelectable(method: AmazonFulfillmentMethodIntent): boolean {
  return method === AMAZON_FULFILLMENT_MERCHANT;
}

export function mapCanonicalPackageToAmazonListingAttributes(
  parse: AmazonMerchantShippingParseOk,
  facts: ShippingDimensions,
): Record<string, unknown> {
  const attributes: Record<string, unknown> = {};
  if (parse.itemPackageWeight.present && facts.weightKg != null) {
    attributes.item_package_weight = [
      { unit: "kilograms", value: facts.weightKg, marketplace_id: AMAZON_AU_MARKETPLACE_ID },
    ];
  }
  if (
    parse.itemPackageDimensions.present
    && facts.lengthCm != null
    && facts.widthCm != null
    && facts.heightCm != null
  ) {
    attributes.item_package_dimensions = [
      {
        length: { unit: "centimeters", value: facts.lengthCm },
        width: { unit: "centimeters", value: facts.widthCm },
        height: { unit: "centimeters", value: facts.heightCm },
        marketplace_id: AMAZON_AU_MARKETPLACE_ID,
      },
    ];
  }
  return attributes;
}

function amazonRow(
  status: MarketplaceReadinessStatus,
  summary: string,
  missingFields: ProviderReadiness["missingFields"] = [],
): ProviderReadiness {
  return {
    provider: AMAZON_PROVIDER,
    displayName: "Amazon",
    status,
    missingFields,
    summary,
  };
}

export function evaluateAmazonMerchantShippingReadiness(opts: {
  sellerSpecific: boolean;
  schema: AmazonSchemaIdentityLite | null | undefined;
  parse: AmazonMerchantShippingParseResult;
  listing: AmazonListingShippingConfig;
  packageFacts?: ShippingDimensions | null;
  schemaAuthoritative: boolean;
}): ProviderReadiness {
  if (!opts.sellerSpecific) {
    return amazonRow("NOT_EVALUATED", "Generic Product Type Definition cannot establish Amazon shipping readiness");
  }
  if (!opts.schemaAuthoritative) {
    return amazonRow("NOT_EVALUATED", "Amazon seller-specific Product Type Definition is not authoritative");
  }
  if (opts.schema && opts.schema.marketplaceId !== AMAZON_AU_MARKETPLACE_ID) {
    return amazonRow("NOT_EVALUATED", "Amazon shipping evaluation is limited to marketplace A39IBJ37TRP1C6");
  }
  const parsed = opts.parse;
  if (!parsed.ok) {
    return amazonRow("PROVIDER_ERROR", parsed.error ?? "merchant_shipping_group is malformed");
  }

  if (opts.listing.fulfillmentMethod === AMAZON_FULFILLMENT_FBA) {
    return amazonRow("NEEDS_MAPPING", "Fulfilled by Amazon is not available");
  }

  if (parsed.itemPackageWeight.required || parsed.itemPackageDimensions.required) {
    const facts = opts.packageFacts ?? { weightKg: null, lengthCm: null, widthCm: null, heightCm: null };
    const missing = missingShippingFields(facts);
    const needWeight = parsed.itemPackageWeight.required && missing.includes("weight");
    const needDims = parsed.itemPackageDimensions.required
      && (missing.includes("length") || missing.includes("width") || missing.includes("height"));
    if (needWeight || needDims) {
      const labels: ProviderReadiness["missingFields"] = [];
      if (needWeight) labels.push({ field: "weight", label: "item_package_weight" });
      if (needDims) labels.push({ field: "dimensions", label: "item_package_dimensions" });
      return amazonRow("MISSING_REQUIRED_DATA", "Missing required Amazon package facts", labels);
    }
  }

  if (parsed.leadTimeToShip.required) {
    return amazonRow(
      "MISSING_REQUIRED_DATA",
      "Amazon listing handling time is required by the Product Type Definition and has no listing value",
      [{ field: "name", label: "lead_time_to_ship_max_days" }],
    );
  }

  if (opts.listing.policyIntent === AMAZON_SHIPPING_POLICY_UNSET) {
    return amazonRow("MISSING_REQUIRED_DATA", "Amazon shipping policy is not chosen", [
      { field: "name", label: "merchant_shipping_group" },
    ]);
  }

  if (opts.listing.policyIntent === AMAZON_SHIPPING_POLICY_DEFAULT) {
    if (parsed.merchantShippingGroupRequired) {
      return amazonRow(
        "MISSING_REQUIRED_DATA",
        "Amazon Product Type Definition requires an explicit shipping policy",
        [{ field: "name", label: "merchant_shipping_group" }],
      );
    }
    if (opts.listing.merchantShippingGroupKey) {
      return amazonRow("NEEDS_APPROVAL", "Amazon default shipping policy must not store a template key");
    }
    return amazonRow("READY", "Ready");
  }

  const key = opts.listing.merchantShippingGroupKey?.trim() ?? "";
  if (!key) {
    return amazonRow("MISSING_REQUIRED_DATA", "Amazon shipping policy key is missing", [
      { field: "name", label: "merchant_shipping_group" },
    ]);
  }
  const current = parsed.templates.find((row) => row.key === key);
  if (!current) {
    return amazonRow("NEEDS_APPROVAL", "Selected Amazon shipping policy is no longer valid");
  }
  return amazonRow("READY", "Ready");
}

export type AmazonDormantShippingUx = {
  fulfilmentEnabledLabel: string;
  fulfilmentFbaLabel: string;
  fulfilmentFbaEnabled: false;
  shippingPolicyLabel: string;
  amazonAuthorityNote: string;
  manageInAmazon: string;
  connectToLoadPolicies: string;
  noPoliciesFromAmazon: string;
  amazonDefaultLabel: string;
  amazonDefaultHelp: string;
};

export function amazonDormantShippingUx(): AmazonDormantShippingUx {
  return {
    fulfilmentEnabledLabel: "I ship orders myself",
    fulfilmentFbaLabel: "Fulfilled by Amazon",
    fulfilmentFbaEnabled: false,
    shippingPolicyLabel: "Amazon shipping policy",
    amazonAuthorityNote: "Amazon manages shipping regions, rates and delivery estimates.",
    manageInAmazon: "Manage shipping in Amazon Seller Central",
    connectToLoadPolicies: "Connect Amazon to load your shipping policies.",
    noPoliciesFromAmazon:
      "No shipping policies were returned by Amazon. Set up shipping in Seller Central and refresh.",
    amazonDefaultLabel: "Use Amazon default shipping policy",
    amazonDefaultHelp:
      "Amazon applies the default shipping template for new SKUs. Changing that default later does not update existing listings. Tradivo does not know which template is the default.",
  };
}

export function amazonShippingPolicySelectOptions(
  parse: AmazonMerchantShippingParseOk,
): { value: string; label: string }[] {
  return parse.templates.map((row) => ({ value: row.key, label: row.displayName }));
}

export function assertNoInventedAmazonDefaultKey(listing: AmazonListingShippingConfig): boolean {
  return listing.policyIntent !== AMAZON_SHIPPING_POLICY_DEFAULT || listing.merchantShippingGroupKey == null;
}
